import React, { useEffect } from 'react';
import { Prospect } from '../../shared/types';
import { Calendar, Users, MapPin, Check, Share2, Heart, Clock, Star } from 'lucide-react';

interface PackageHotpageDemoProps {
  prospect: Prospect;
  packageSlug: string;
}

export const PackageHotpageDemo: React.FC<PackageHotpageDemoProps> = ({ prospect, packageSlug }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    if (prospect.theme) {
      const root = document.documentElement;
      root.style.setProperty('--ym-prospect-primary', prospect.theme.primary_color);
      if (prospect.theme.bg_color) root.style.setProperty('--ym-prospect-bg', prospect.theme.bg_color);
      if (prospect.theme.text_color) root.style.setProperty('--ym-prospect-text', prospect.theme.text_color);
      if (prospect.theme.surface_color) root.style.setProperty('--ym-prospect-surface', prospect.theme.surface_color);
    }
  }, [prospect]);

  const getPackageName = (slug: string) => {
    return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  return (
    <div className="min-h-screen bg-[var(--ym-prospect-bg)] text-[var(--ym-prospect-text)] font-sans">
      
      {/* Immersive Hero */}
      <section className="relative h-[60vh] min-h-[500px]">
        <div className="absolute inset-0 bg-slate-900">
           {/* Fallback image if canonical data is missing */}
           <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1522083165195-3444bed50e47?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-70 mix-blend-overlay" />
           <div className="absolute inset-0 bg-gradient-to-t from-[var(--ym-prospect-bg)] via-[var(--ym-prospect-bg)]/20 to-transparent" />
        </div>

        {/* Top Navbar overlay */}
        <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-center z-20">
          <div className="w-10 h-10 rounded bg-[var(--ym-prospect-primary)] flex items-center justify-center text-white font-bold text-xl shadow-lg">
            {prospect.trade_name.charAt(0)}
          </div>
          <div className="flex gap-4">
            <button className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center hover:bg-white/40 text-white transition-colors">
              <Heart className="w-5 h-5" />
            </button>
            <button className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center hover:bg-white/40 text-white transition-colors">
              <Share2 className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 z-10">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 bg-[var(--ym-prospect-primary)] text-white text-xs font-bold rounded-full">
                GRUPO EXCLUSIVO
              </span>
              <span className="px-3 py-1 bg-white/20 backdrop-blur-md text-white text-xs font-bold rounded-full flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Destino Internacional
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold font-heading mb-4 text-white">
              {getPackageName(packageSlug)}
            </h1>
            <p className="text-lg md:text-xl text-white/80 max-w-2xl">
              Uma experiência imersiva e inesquecível criada sob medida pelos especialistas da {prospect.trade_name}.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Grid */}
      <section className="max-w-5xl mx-auto px-4 md:px-12 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          
          {/* Left Column (Details) */}
          <div className="lg:col-span-2 space-y-12">
            
            {/* Quick Stats */}
            <div className="flex flex-wrap gap-8 py-6 border-y border-[var(--ym-prospect-surface)]/20">
              <div className="flex items-center gap-3">
                <Calendar className="w-6 h-6 text-[var(--ym-prospect-primary)]" />
                <div>
                  <div className="text-xs opacity-60 uppercase font-bold">Duração</div>
                  <div className="font-semibold">12 Dias, 11 Noites</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Users className="w-6 h-6 text-[var(--ym-prospect-primary)]" />
                <div>
                  <div className="text-xs opacity-60 uppercase font-bold">Tamanho do Grupo</div>
                  <div className="font-semibold">Vagas Limitadas</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Star className="w-6 h-6 text-[var(--ym-prospect-primary)]" />
                <div>
                  <div className="text-xs opacity-60 uppercase font-bold">Categoria</div>
                  <div className="font-semibold">Premium</div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold font-heading mb-4">Sobre a Experiência</h2>
              <p className="opacity-80 leading-relaxed mb-6">
                Este wireframe demonstra como a {prospect.trade_name} pode ter landing pages específicas (Hotpages) para cada um de seus pacotes e grupos. Em vez de enviar PDFs estáticos pelo WhatsApp, os clientes recebem uma página rica, dinâmica, que vende a emoção da viagem e já capta o lead diretamente para o CRM.
              </p>
              
              <h3 className="font-bold mb-4 mt-8">O que está incluso:</h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  'Aéreo ida e volta (saindo de SP)',
                  'Hospedagem em hotéis 4 e 5 estrelas',
                  'Café da manhã diário',
                  'Guia acompanhante desde o Brasil',
                  'Transfers exclusivos',
                  'Seguro Viagem GTA Premium'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 opacity-80">
                    <Check className="w-5 h-5 text-emerald-500 shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right Column (Floating Action Card) */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-[var(--ym-prospect-surface)] border border-white/10 rounded-2xl p-6 shadow-xl">
              <div className="text-center mb-6">
                <div className="text-sm opacity-60 uppercase font-bold mb-2">Investimento</div>
                <div className="text-3xl font-bold">Sob Consulta</div>
                <p className="text-xs opacity-50 mt-2">Valores podem sofrer alteração conforme disponibilidade e câmbio.</p>
              </div>

              <div className="space-y-4">
                <button className="w-full py-4 bg-[var(--ym-prospect-primary)] text-white font-bold rounded-xl hover:opacity-90 transition-opacity">
                  Solicitar Cotação
                </button>
                <button className="w-full py-4 border border-[var(--ym-prospect-primary)] text-[var(--ym-prospect-primary)] font-bold rounded-xl hover:bg-[var(--ym-prospect-primary)]/10 transition-colors">
                  Falar no WhatsApp
                </button>
              </div>

              <div className="mt-6 pt-6 border-t border-black/5 dark:border-white/5 flex items-center justify-center gap-2 text-sm opacity-70">
                <Clock className="w-4 h-4" /> Resposta em até 2 horas.
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};
